/* eslint-disable @typescript-eslint/no-explicit-any */
import { call, put } from "redux-saga/effects";
import { createLogger } from "../../features/notifications/utils/logger";
import type { ActionCreatorWithPayload } from "@reduxjs/toolkit";

const logger = createLogger("Redux");

/**
 * Wraps a saga worker with standardized error handling
 * @param workerName - Name of the worker for logging
 * @param asyncFn - Generator function that performs the async operation
 * @param successAction - Action creator to dispatch on success
 * @param failureAction - Action creator to dispatch on failure
 * @param defaultError - Default error message if none is provided
 */
export function* withErrorHandling<TResult, TSuccess, TFailure>(
  workerName: string,
  asyncFn: () => Generator<any, TResult, any>,
  successAction: ActionCreatorWithPayload<TSuccess>,
  failureAction: ActionCreatorWithPayload<TFailure>,
  defaultError: string = "Operation failed",
  extraFailureContext: Record<string, unknown> = {},
) {
  try {
    const result: TResult = yield call(asyncFn);
    yield put(successAction(result as any));
  } catch (err: unknown) {
    logger.error(`[${workerName}]`, err);
    const message =
      (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
      (err as { message?: string })?.message ||
      defaultError;
    yield put(failureAction({ error: message, ...extraFailureContext } as any));
  }
}
